import { Injectable, inject } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpContext,
  HttpContextToken,
  HttpBackend,
  HttpParams
} from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { catchError, switchMap, tap, concatMap, map } from 'rxjs/operators';
import { DISABLE_AUTH_INTERCEPTOR } from '../../http-interceptors/auth-interceptor';
import { environment } from '../../environments/environment';

export interface PresignedUrlResponse {
  document_id: string;
  job_id: string;
  s3_bucket: string;
  s3_key: string;
  s3_uri: string;
  upload_url: string;
  content_type: string;
}

export interface ExtractResponse {
  job_id: string;
  s3_input: string;
  s3_output: string;
  invocation_arn: string;
}

export interface TranscriptMetadata {
  uploadDate: string;
  source: string;
  [key: string]: any; // Allow for additional custom metadata
}

export interface TranscriptRequestData {
  fileName: string;
  title: string;
  metadata: TranscriptMetadata;
}

export interface TranscriptStatusUpdate {
  id: string;
  status: string;
  s3ResultLocation: string;
}

@Injectable({
  providedIn: 'root'
})
export class TranscriptApiService {
  private http = inject(HttpClient);

  private apiUrl = environment.urls.transcriptApi;
  private defaultTitlePrefix = 'Transcript';

  /**
   * Upload a PDF file and return the presigned URL response after upload completes
   * @param fileName The name of the PDF file
   * @param blob The binary Blob of the PDF file
   * @param tenantId The tenant identifier
   * @returns Observable with the presigned URL response (after upload completes)
   * @deprecated Use getPresignedUrl and uploadFileToS3 separately instead
   */
  uploadPdf(fileName: string, blob: Blob, tenantId: string): Observable<PresignedUrlResponse> {
    return this.getPresignedUrl(fileName, tenantId).pipe(
      concatMap(presignedUrlResponse => {
        // Store the presigned URL response
        return this.uploadFileToS3(
          blob,
          presignedUrlResponse.upload_url,
          presignedUrlResponse.content_type
        ).pipe(
          // After upload completes, return the original presigned URL response
          map(() => presignedUrlResponse)
        );
      })
    );
  }

  /**
   * Get a presigned URL for uploading a PDF file to S3
   * @param name The filename to use when storing the file in S3
   * @param tenantId The tenant identifier
   * @param transcriptId Optional transcript identifier (if already created)
   * @returns Observable with presigned URL response containing document_id, job_id, s3_bucket, s3_key, and upload_url
   */
  getPresignedUrl(
    name: string,
    tenantId: string,
    transcriptId?: string
  ): Observable<PresignedUrlResponse> {
    let params = new HttpParams()
      .set('filename', name)
      .set('backend_queue', 'true')
      .set('tenant_id', tenantId);

    if (transcriptId) {
      params = params.set('transcript_id', transcriptId);
    }

    const requestUrl = this.apiUrl + '/upload-transcript/url';
    return this.http.get<PresignedUrlResponse>(requestUrl, { params }).pipe(
      catchError(error => {
        console.error('Error getting presigned URL:', error);
        return throwError(() => new Error(`Failed to get presigned URL: ${error.message}`));
      })
    );
  }

  /**
   * Upload a file to S3 using the presigned URL
   * @param blob The binary Blob of the file
   * @param presignedUrl The presigned URL for uploading
   * @param contentType The MIME type of the file being uploaded (e.g., 'application/pdf').
   * @returns Observable of the upload response
   */
  uploadFileToS3(blob: Blob, presignedUrl: string, contentType: string): Observable<object> {
    // Convert base64 to binary data
    return new Observable(observer => {
      try {
        const headers = new HttpHeaders({
          'Content-Type': contentType
        });

        //Send the binary data using HttpClient
        this.http
          .put(presignedUrl, blob, {
            headers: headers,
            context: new HttpContext().set(DISABLE_AUTH_INTERCEPTOR, true)
          })

          .subscribe({
            next: response => {
              observer.next(response);
              observer.complete();
            },
            error: error => {
              console.error('Error uploading file to S3:', error);
              observer.error(new Error(`Failed to upload file to S3: ${error.message}`));
            }
          });
      } catch (error) {
        observer.error(new Error(`Failed to convert base64 to binary: ${error}`));
      }
    });
  }

  /**
   * Set a custom API URL (useful for testing or different environments)
   * @param url The new API URL
   */
  setApiUrl(url: string): void {
    this.apiUrl = url;
  }
}
